import type { Message, RelatedEntity, User } from "../types"

export class MessageApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public originalError?: unknown
  ) {
    super(message)
    this.name = 'MessageApiError'
  }
}

export interface ApiConfig {
  apiEndpoint: string
  userToken?: string | (() => string)
}

export interface SendMessageData {
  content: string
  contentType: 'plain' | 'html'
  entityTypeId: string
  entityId: string
  isVisibleToClient: boolean
  isImportant?: boolean
  parentId?: string
}

export interface FetchMessagesParams {
  entities: Array<{ entityTypeId: string; entityId: string }>
}

export interface FetchRelatedEntitiesParams {
  entityTypeId: string
  entityId: string
}

export interface FetchMessageCountParams {
  entityTypeId: string
  entityId: string
}

export class MessageApiService {
  private config: ApiConfig

  constructor(config: ApiConfig) {
    this.config = config
  }

  /**
   * Get the current user token value by calling the token function
   */
  private getCurrentUserToken(): string {
    if (!this.config.userToken) {
      throw new MessageApiError("UserToken not configured in API service")
    }

    return typeof this.config.userToken === 'function' ? this.config.userToken() : this.config.userToken
  }

  // Convenience methods that use the configured userToken
  async fetchUserWithToken(): Promise<User> {
    return this.fetchUser()
  }

  async fetchRelatedEntitiesWithToken(entityTypeId: string, entityId: string): Promise<RelatedEntity[]> {
    return this.fetchRelatedEntities({ entityTypeId, entityId })
  }

  async fetchMessagesWithToken(entities: Array<{ entityTypeId: string; entityId: string }>): Promise<Message[]> {
    return this.fetchMessages({ entities })
  }

  async fetchMessageCountWithToken(entityTypeId: string, entityId: string): Promise<number> {
    return this.fetchMessageCount({ entityTypeId, entityId })
  }

  async fetchUnreadCountWithToken(entityTypeId: string, entityId: string): Promise<number> {
    return this.fetchUnreadCount({ entityTypeId, entityId })
  }

  async sendMessageWithToken(messageData: SendMessageData): Promise<Message> {
    return this.sendMessage(messageData)
  }

  /**
   * Create headers with Authorization Bearer token
   */
  private createHeaders(userToken: string): Record<string, string> {
    if (!userToken) {
      return {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    } else {
      return {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    }
  }

  /**
   * Fetch user data by token
   */
  async fetchUser(): Promise<User> {
    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/user`, {
        headers: this.createHeaders(userToken)
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch user data - status: ${response.status}`,
          response.status
        )
      }

      const userData = await response.json()
      return userData
    } catch (error) {
      console.error("Failed to fetch user data:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch user data", undefined, error)
    }
  }

  /**
   * Fetch related entities for a given entity
   */
  async fetchRelatedEntities(params: FetchRelatedEntitiesParams): Promise<RelatedEntity[]> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams({
        entityTypeId: params.entityTypeId,
        entityId: params.entityId
      })
      const response = await fetch(
        `${this.config.apiEndpoint}/related-entities?${queryParams}`,
        {
          headers: this.createHeaders(userToken)
        }
      )

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch related entities - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      return data.relatedEntities
    } catch (error) {
      console.error("Failed to fetch related entities:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch related entities", undefined, error)
    }
  }

  /**
   * Organize flat messages into threaded structure
   */
  private organizeMessagesIntoThreads(messages: Message[]): Message[] {
    if (!messages?.length) return []
    // Clone and ensure replies array
    const map = new Map<string, Message>()
    for (const m of messages) {
      map.set(m.id, { ...m, replies: Array.isArray(m.replies) ? m.replies : [] })
    }
    const roots: Message[] = []
    const byTime = (a: Message, b: Message) =>
      new Date(a.createdDateTime as any).getTime() - new Date(b.createdDateTime as any).getTime()
    // Stitch parents/children regardless of order
    for (const msg of Array.from(map.values())) {
      const parent = msg.parentId ? map.get(msg.parentId) : undefined
      if (parent) {
        ; (parent.replies ?? (parent.replies = [])).push(msg)
      } else {
        roots.push(msg)
      }
    }
    // Stable sort roots and each replies list by time
    const sortRec = (list: Message[]) => {
      list.sort(byTime)
      for (const child of list) if (child.replies?.length) sortRec(child.replies)
    }
    sortRec(roots)
    return roots
  }

  /**
   * Fetch messages for selected entities
   */
  async fetchMessages(params: FetchMessagesParams): Promise<Message[]> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams(
        params.entities.map((entity) => [
          'entities',
          `${entity.entityTypeId}:${entity.entityId}`
        ])
      )
      const response = await fetch(`${this.config.apiEndpoint}/messages?${queryParams}`, {
        headers: this.createHeaders(userToken)
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch messages - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()

      // Organize flat messages into threaded structure
      const threadedMessages = this.organizeMessagesIntoThreads(data.messages)

      return threadedMessages
    } catch (error) {
      console.error("Failed to fetch messages:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch messages", undefined, error)
    }
  }

  /**
   * Send a new message or reply
   */
  async sendMessage(messageData: SendMessageData): Promise<Message> {
    try {
      const userToken = this.getCurrentUserToken()
      const response = await fetch(`${this.config.apiEndpoint}/messages`, {
        method: "POST",
        headers: this.createHeaders(userToken),
        body: JSON.stringify(messageData),
      })

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to send message - status: ${response.status}`,
          response.status
        )
      }

      const newMessage = await response.json()
      return newMessage
    } catch (error) {
      console.error("Failed to send message:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to send message", undefined, error)
    }
  }

  /**
   * Fetch message count for an entity
   */
  async fetchMessageCount(params: FetchMessageCountParams): Promise<number> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams({
        entityTypeId: params.entityTypeId,
        entityId: params.entityId
      })
      const response = await fetch(
        `${this.config.apiEndpoint}/messages/message-count?${queryParams}`,
        {
          headers: this.createHeaders(userToken)
        }
      )

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch message count - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      return data.count || 0
    } catch (error) {
      console.error("Failed to fetch message count:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch message count", undefined, error)
    }
  }

  /**
   * Fetch unread message count for an entity
   */
  async fetchUnreadCount(params: FetchMessageCountParams): Promise<number> {
    try {
      const userToken = this.getCurrentUserToken()
      const queryParams = new URLSearchParams({
        entityTypeId: params.entityTypeId,
        entityId: params.entityId
      })
      const response = await fetch(
        `${this.config.apiEndpoint}/messages/unread-count?${queryParams}`,
        {
          headers: this.createHeaders(userToken)
        }
      )

      if (!response.ok) {
        throw new MessageApiError(
          `Failed to fetch unread count - status: ${response.status}`,
          response.status
        )
      }

      const data = await response.json()
      return data.count || 0
    } catch (error) {
      console.error("Failed to fetch unread count:", error)
      if (error instanceof MessageApiError) {
        throw error
      }
      throw new MessageApiError("Failed to fetch unread count", undefined, error)
    }
  }
}

// Factory function to create API service with default endpoints
export function createMessageApiService(config: Partial<ApiConfig> = {}): MessageApiService {
  const defaultConfig: ApiConfig = {
    apiEndpoint: '/api/messages',
    ...config
  }

  return new MessageApiService(defaultConfig)
}
